/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Container, Grid, makeStyles } from '@material-ui/core';
import LinearProgress from '@material-ui/core/LinearProgress';
import { ChevronLeft } from '@material-ui/icons';
import { Link } from 'react-router-dom';

import TemplateColumns from './TemplateColumns';
import TemplateListHeader from './TemplateListHeader';
import TableDense from '../../components/common/TableDense';
import useBlocRefHook from '../../utils/useBlocRef.hook';

const useStyle = makeStyles((theme) => {
  return {
    linkBack: {
      alignItems: 'center',
      color: theme.palette.primary.main,
      display: 'flex',
      flexWrap: 'nowrap',
      fontSize: '14px',
      fontWeight: '600',
      justifyContent: 'start',
      textDecoration: 'none',
    },

    linkBackIcon: {
      marginRight: '0px',
    },

    tableActionColumnContainer: {
      alignItems: 'center',
      color: theme.palette.grey['500'],
      cursor: 'pointer',
      display: 'flex',
      flexWrap: 'nowrap',
      fontSize: '12px',
      fontWeight: 600,
      justifyContent: 'start',
      textDecoration: 'none',
    },
    tableActionIcon: {
      color: theme.palette.grey['500'],
      fontSize: '20px',
      marginRight: '4px',
    },
    tableDescription: {
      color: theme.palette.grey['500'],
    },
    tablePrivacyGlobal: {
      color: '#30aae1',
    },
    tablePrivacyUser: {
      alignItems: 'center',
      color: theme.palette.grey['500'],
      display: 'flex',
      flexWrap: 'nowrap',
    },
    tablePrivacyUserIcon: {
      color: theme.palette.grey['500'],
      fontSize: '20px',
      marginRight: '4px',
    },
    tablePrivacyWorkspace: {
      color: theme.palette.primary.main,
    },
  };
});

const TemplateList = () => {
  const blocRef = useBlocRefHook('templatesBloc');
  const classes = useStyle();

  const [userDetails, setUserDetails] = useState({});
  const [userWorkspace, setUserWorkspace] = useState({});

  const [templates, setTemplates] = useState([]);
  const [totalData, setTotalData] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [tags, setTags] = useState([]);

  const [loading, setLoading] = useState(false);
  const [isTagsLoading, setIsTagsLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [selectedTags, setSelectedTags] = useState('');
  const [selectedPrivacy, setSelectedPrivacy] = useState('');

  const [tableOptions, setTableOptions] = useState({});

  const handleChangeSearch = useCallback((event) => setSearch(event.target.value), []);
  const handleChangeTag = useCallback((_e, value) => setSelectedTags(value), []);
  const handleChangePrivacy = useCallback((event) => setSelectedPrivacy(event.target.value), []);
  const handleClearClick = useCallback(() => {
    setSelectedPrivacy('');
    setSearch('');
    setSelectedTags('');

    blocRef.current.getTemplates({ limit: 15, page: 1, privacy: '', search: '', tags: '' });
  }, []);

  const handleSearch = () => {
    blocRef.current.getTemplates({ limit: 15, page: 1, privacy: selectedPrivacy, search, tags: selectedTags });
  };

  const handleChangePage = useCallback(
    (page) => {
      blocRef.current.getTemplates({ limit: 15, page, privacy: selectedPrivacy, search, tags: selectedTags });
    },
    [selectedPrivacy, search, selectedTags]
  );

  const handleSearchKeyDown = (event) => {
    const { key } = event;
    if (key === 'Enter') {
      handleSearch();
    }
  };

  const userName = useMemo(() => {
    return userDetails?.name || '';
  }, [userDetails]);

  const workspaceName = useMemo(() => {
    return userWorkspace?.name || '';
  }, [userWorkspace]);

  const isMediphanyWorkspace = useMemo(() => {
    return userWorkspace?.isMediphanyWorkspace || false;
  }, [userWorkspace]);

  useEffect(() => {
    const userDetailSub = blocRef.current.userDetails$.subscribe(setUserDetails);
    const userWorkspaceSub = blocRef.current.userWorkspace$.subscribe(setUserWorkspace);
    const templatesSub = blocRef.current.templates$.subscribe(setTemplates);
    const totalPagesSub = blocRef.current.totalData$.subscribe(setTotalData);
    const currentPageSub = blocRef.current.currentPage$.subscribe(setCurrentPage);
    const tagsSub = blocRef.current.tags$.subscribe(setTags);
    const loadingSub = blocRef.current.isLoading$.subscribe(setLoading);
    const isTagsLoadingSub = blocRef.current.isTagsLoading$.subscribe(setIsTagsLoading);

    return () => {
      blocRef.current.dispose();

      userDetailSub.unsubscribe();
      userWorkspaceSub.unsubscribe();
      templatesSub.unsubscribe();
      totalPagesSub.unsubscribe();
      currentPageSub.unsubscribe();
      tagsSub.unsubscribe();
      loadingSub.unsubscribe();
      isTagsLoadingSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    handleSearch();
  }, [selectedPrivacy, selectedTags]);

  useEffect(() => {
    setTableOptions({
      count: totalData,
      limit: 15,
      onTableChange: (action, tableState) => {
        if (action === 'changePage') {
          handleChangePage(tableState.page + 1);
        }
      },
      page: currentPage - 1,
    });
  }, [currentPage, totalData, handleChangePage]);

  return (
    <Grid container component={Container} style={{ display: 'flex', marginTop: '30px', textAlign: 'left', width: '100vw' }}>
      {/* GO BACK ICON */}
      <Grid item xs={12}>
        <Grid container>
          <Grid item xs={12}>
            <div style={{ display: 'inline-block' }}>
              <Link to="/resources" className={classes.linkBack}>
                <ChevronLeft className={classes.linkBackIcon} />
                <span style={{ lineHeight: 1 }}>go back</span>
              </Link>
            </div>
          </Grid>
        </Grid>
      </Grid>
      {/* GO BACK ICON END */}

      <Grid item xs={12}>
        <TemplateListHeader
          selectedPrivacy={selectedPrivacy}
          selectedTags={selectedTags}
          handleChangePrivacy={handleChangePrivacy}
          handleChangeSearch={handleChangeSearch}
          handleChangeTag={handleChangeTag}
          handleSearch={handleSearch}
          isMediphanyWorkspace={isMediphanyWorkspace}
          handleSearchKeyDown={handleSearchKeyDown}
          loading={loading}
          search={search}
          tags={tags}
          isTagsLoading={isTagsLoading}
          onClear={handleClearClick}
        />
      </Grid>

      <Grid item xs={12}>
        <div style={{ marginTop: '24px' }}>
          {loading ? <LinearProgress /> : null}
          <TableDense
            tableId="template-list"
            options={tableOptions}
            data={templates || []}
            columns={TemplateColumns({ classes, currentUserName: userName, isMediphanyWorkspace, workspaceName })}
          />
        </div>
      </Grid>
    </Grid>
  );
};

export default TemplateList;
